import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Textarea } from '../ui/textarea'
import { apiErrorMessage } from './billing-errors'
import { useAdminBillingAccount, usePutAdminBillingAccount } from '../../hooks/use-admin-billing'

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7)
}

/** BillingAccountSection is the account form of one organization (spec 2026-10-05-13). */
export function BillingAccountSection({ orgUid }: { orgUid: string }) {
  const { t } = useTranslation('admin')
  const { data, isLoading } = useAdminBillingAccount(orgUid)
  const put = usePutAdminBillingAccount()
  const [billable, setBillable] = useState(false)
  const [start, setStart] = useState(currentMonth())
  const [end, setEnd] = useState('')
  const [note, setNote] = useState('')
  const [plan, setPlan] = useState<'monthly' | 'annual'>('monthly')
  const [planStart, setPlanStart] = useState(currentMonth())
  const [terms, setTerms] = useState('30')
  const [vat, setVat] = useState('20')
  const [ref, setRef] = useState('')
  const [discountNum, setDiscountNum] = useState('10')
  const [discountDen, setDiscountDen] = useState('12')
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    if (!data) return
    const acc = data.account
    setBillable(acc?.billable ?? false)
    setStart(acc?.start_month.slice(0, 7) ?? currentMonth())
    setEnd(acc?.end_month?.slice(0, 7) ?? '')
    setNote(acc?.note ?? '')
    setPlan(acc?.plan ?? 'monthly')
    setPlanStart(acc?.plan_start_month.slice(0, 7) ?? currentMonth())
    setTerms(String(acc?.payment_terms_days ?? 30))
    setVat(String((acc?.vat_rate_bp ?? 2000) / 100))
    setRef(acc?.external_customer_ref ?? '')
    setDiscountNum(String(acc?.annual_discount_num ?? 10))
    setDiscountDen(String(acc?.annual_discount_den ?? 12))
  }, [data])

  const save = () => {
    setMessage(null)
    put.mutate(
      {
        params: { path: { orgUid } },
        body: {
          billable,
          start_month: `${start}-01`,
          end_month: end ? `${end}-01` : null,
          note: note || null,
          plan,
          plan_start_month: `${planStart}-01`,
          payment_terms_days: Number(terms),
          vat_rate_bp: Math.round(Number(vat.replace(',', '.')) * 100),
          external_customer_ref: ref || null,
          annual_discount_num: Number(discountNum),
          annual_discount_den: Number(discountDen),
        },
      },
      {
        onSuccess: () => setMessage({ ok: true, text: t('billing.account.saved') }),
        onError: (e) =>
          setMessage({ ok: false, text: apiErrorMessage(e, t('billing.account.save_error')) }),
      },
    )
  }

  return (
    <section className="space-y-4" data-testid="billing-account">
      <div>
        <h2 className="text-lg font-semibold">{t('billing.account.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('billing.account.help')}</p>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 space-y-4">
        {isLoading ? (
          <div className="text-muted-foreground">{t('billing.loading')}</div>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={billable} onCheckedChange={setBillable} data-testid="billing-account-billable" />
              {t('billing.account.billable')}
            </label>
            <div className="flex flex-wrap gap-4">
              <div className="space-y-1">
                <Label>{t('billing.account.start_month')}</Label>
                <Input type="month" value={start} onChange={(e) => setStart(e.target.value)} className="w-44" data-testid="billing-account-start" />
              </div>
              <div className="space-y-1">
                <Label>{t('billing.account.end_month')}</Label>
                <Input type="month" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44" data-testid="billing-account-end" />
              </div>
            </div>
            <div className="flex flex-wrap gap-4" data-testid="billing-account-invoicing">
              <div className="space-y-1">
                <Label>{t('billing.account.plan')}</Label>
                <Select value={plan} onValueChange={(v) => setPlan(v as 'monthly' | 'annual')}>
                  <SelectTrigger className="w-44" data-testid="billing-account-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">{t('billing.account.plan_monthly')}</SelectItem>
                    <SelectItem value="annual">{t('billing.account.plan_annual')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>{t('billing.account.plan_start')}</Label>
                <Input type="month" value={planStart} onChange={(e) => setPlanStart(e.target.value)} className="w-44" data-testid="billing-account-plan-start" />
              </div>
              <div className="space-y-1">
                <Label>{t('billing.account.terms')}</Label>
                <Input type="number" min={0} value={terms} onChange={(e) => setTerms(e.target.value)} className="w-32" data-testid="billing-account-terms" />
              </div>
              <div className="space-y-1">
                <Label>{t('billing.account.vat')}</Label>
                <Input value={vat} onChange={(e) => setVat(e.target.value)} inputMode="decimal" className="w-28" data-testid="billing-account-vat" />
              </div>
              <div className="space-y-1">
                <Label>{t('billing.account.discount')}</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min={1} value={discountNum} onChange={(e) => setDiscountNum(e.target.value)} className="w-20" data-testid="billing-account-discount-num" />
                  <span aria-hidden>/</span>
                  <Input type="number" min={1} value={discountDen} onChange={(e) => setDiscountDen(e.target.value)} className="w-20" data-testid="billing-account-discount-den" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>{t('billing.account.external_ref')}</Label>
                <Input value={ref} onChange={(e) => setRef(e.target.value)} className="w-52" data-testid="billing-account-external-ref" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>{t('billing.account.note')}</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="billing-account-note" />
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={save} disabled={put.isPending || !start} data-testid="billing-account-save">
                {t('billing.account.save')}
              </Button>
              {message && (
                <span className={message.ok ? 'text-sm text-muted-foreground' : 'text-sm text-destructive'} data-testid="billing-account-message">
                  {message.text}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  )
}
